import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, ArrowLeft, Film } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

export default function AdminChapters() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", order: 0 });

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchData = async () => {
    const [courseRes, chapterRes] = await Promise.all([
      api.get(`/courses/${courseId}`),
      api.get(`/courses/${courseId}/chapters`),
    ]);
    setCourse(courseRes.data);
    setChapters(chapterRes.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, order: parseInt(form.order) || 0 };
    if (editing) {
      await api.put(`/chapters/${editing}`, payload);
    } else {
      await api.post(`/courses/${courseId}/chapters`, payload);
    }
    setShowForm(false);
    setEditing(null);
    setForm({ title: "", order: 0 });
    fetchData();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this chapter and all its videos?")) {
      await api.delete(`/chapters/${id}`);
      fetchData();
    }
  };

  const inputClass = "w-full bg-slate-50 border-b-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-chapters-page">
        <button onClick={() => navigate("/admin/courses")} className="flex items-center gap-1 text-sm text-[#0284C7] font-bold mb-3">
          <ArrowLeft size={16} /> Back to Courses
        </button>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Chapters</h1>
            <p className="text-xs text-slate-500">{course?.title}</p>
          </div>
          <button
            data-testid="add-chapter-btn"
            onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ title: "", order: 0 }); }}
            className="bg-[#0284C7] text-white font-bold py-2 px-4 text-sm flex items-center gap-2 transition-none"
          >
            <Plus size={16} /> Add
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white p-4 mb-4 space-y-3" data-testid="chapter-form">
            <input data-testid="chapter-title-input" placeholder="Chapter Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} required />
            <input data-testid="chapter-order-input" placeholder="Order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={inputClass} />
            <button data-testid="chapter-submit-btn" type="submit" className="bg-[#0284C7] text-white font-bold py-3 px-6 text-sm transition-none">
              {editing ? "Update" : "Create"} Chapter
            </button>
          </form>
        )}

        <div className="space-y-2" data-testid="chapters-list">
          {chapters.map((ch) => (
            <div key={ch.id} className="bg-white p-3">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 bg-slate-900 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {ch.order || "—"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900">{ch.title}</p>
                  <p className="text-xs text-slate-500">{ch.videos?.length || 0} videos</p>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`manage-videos-${ch.id}`} onClick={() => navigate(`/admin/chapters/${ch.id}/videos`)} className="p-2 text-[#0284C7]" title="Manage Videos">
                    <Film size={16} />
                  </button>
                  <button onClick={() => { setForm({ title: ch.title, order: ch.order || 0 }); setEditing(ch.id); setShowForm(true); }} className="p-2 text-slate-500">
                    <Edit size={16} />
                  </button>
                  <button onClick={() => handleDelete(ch.id)} className="p-2 text-red-500">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {chapters.length === 0 && <p className="text-sm text-slate-400 py-4">No chapters yet. Add one above.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
